import {
  getLocalStorage,
  saveLocalStorage,
} from "@elit-hair/elit-tools";
import { DEFAULT_COLOR_SCHEMES, THEMES } from "@elit-hair/elit-ui";
import { createContext, useContext, useState } from "react";

import { DATE_FORMAT, TIME_FORMAT } from "@/constants/date-time";
import { SETTINGS_KEYS } from "@/constants/settings-keys";

// If you add new settings, don't forget to add them in the DTO for the remote-config in backend
// (e.g. services/call-svc/src/dtos/remote-config.dto.ts) so they can be stored and returned by the API.
export type SettingsDefinition = {
  THEME: THEMES;
  COLOR_SCHEME: string;
  LANGUAGE: string;
  DATE_FORMAT: DATE_FORMAT;
  TIME_FORMAT: TIME_FORMAT;
  SIDEBAR_COLLAPSED: boolean;
};

export const defaultSettings: SettingsDefinition = {
  THEME: THEMES.LIGHT,
  COLOR_SCHEME: DEFAULT_COLOR_SCHEMES[0].value,
  LANGUAGE: "en",
  DATE_FORMAT: DATE_FORMAT.DD_MM_YYYY,
  TIME_FORMAT: TIME_FORMAT.TWENTY_FOUR_HOURS,
  SIDEBAR_COLLAPSED: false,
};

export const SettingsContext = createContext<{
  currentSettings: SettingsDefinition;
  setValue: (
    key: SETTINGS_KEYS,
    value: SettingsDefinition[SETTINGS_KEYS],
  ) => void;
}>({
  currentSettings: defaultSettings,
  setValue: () => {},
});

const loadLocalSettings = (): SettingsDefinition => {
  const settings = { ...defaultSettings };

  (Object.keys(defaultSettings) as Array<SETTINGS_KEYS>).forEach((key) => {
    const value = getLocalSetting(key);
    if (value !== null && value !== undefined) {
      (settings as Record<SETTINGS_KEYS, SettingsDefinition[SETTINGS_KEYS]>)[
        key
      ] = value;
    }
  });

  return settings;
};

export const getLocalSetting = <T extends SettingsDefinition[SETTINGS_KEYS]>(
  key: SETTINGS_KEYS,
) => {
  return getLocalStorage<T>(key, defaultSettings[key] as T);
};

// Socket events
export type SettingsSocketEvents = {
  "config:updated": {
    adminUserId: string;
    config: SettingsDefinition;
    targetUserId: string;
    timestamp: string;
  };
};

export const SettingsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [currentSettings, setCurrentSettings] =
    useState<SettingsDefinition>(loadLocalSettings());

  const setValue = (
    key: SETTINGS_KEYS,
    value: SettingsDefinition[SETTINGS_KEYS],
  ) => {
    saveLocalStorage(key, value);
    setCurrentSettings((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <SettingsContext.Provider value={{ currentSettings, setValue }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  return useContext(SettingsContext);
};
